import QuestionFrame from './QuestionFrame';
import { shortBlooms } from './types';

/**
 * One question in the builder: what the teacher decides about it, and the question itself.
 *
 * The question is not drawn here - the play screen draws it, through `QuestionFrame`, which is also
 * what fills the window when this question is expanded. Expanding does not mount anything new: the
 * same frame grows, so the teacher is looking at the very question they were looking at.
 */
export function QuestionCard(props: {
  /** The engine's whole answer for this question - what the play screen renders from. */
  response: any;
  index: number;
  checked: boolean;
  onToggle: () => void;
  blooms?: string;
  points: number;
  onRefresh?: () => void;
  refreshDisabled?: boolean;
  /** Fill the window with this question - the frame below grows, nothing is re-rendered. */
  onExpand?: () => void;
  /** True while this question is the one filling the window. */
  expanded?: boolean;
  onCollapse?: () => void;
  /** Viewing a saved quiz: the question is shown as it was frozen, and nothing can be changed. */
  readOnly?: boolean;
  /** Show this question's worked explanation - what the teacher means by "the answer". */
  showAnswer?: boolean;
}) {
  return (
    <div className={`aq-card ${props.checked && !props.readOnly ? 'aq-card-checked' : ''}`}>
      <div className="aq-card-head">
        {/* Both spellings are rendered and the stylesheet shows one: a narrow card says Q1 rather
            than dropping the question's number, which is how a teacher refers to it. */}
        {props.readOnly ? (
          <span className="aq-card-number">
            <span className="aq-label-full">Question {props.index + 1}</span>
            <span className="aq-label-short">Q{props.index + 1}</span>
          </span>
        ) : (
          <label className="aq-check">
            <input type="checkbox" checked={props.checked} onChange={props.onToggle} />
            <span className="aq-label-full">Question {props.index + 1}</span>
            <span className="aq-label-short">Q{props.index + 1}</span>
          </label>
        )}

        <div className="aq-card-meta">
          {/* Both spellings are rendered and the stylesheet shows one: on a card too narrow for the
              word, the level still has to be there - it is how a teacher judges what a question is
              worth - so it is shortened rather than dropped. */}
          {props.blooms ? (
            <>
              <span className="aq-blooms aq-blooms-full">{props.blooms}</span>
              <span className="aq-blooms aq-blooms-short">{shortBlooms(props.blooms)}</span>
            </>
          ) : null}

          {/* What a correct answer is worth, defaulted from Bloom's. Shown, not set: the points are
              changed in the Selected panel, where the quiz is actually put together, and two sets of
              steppers for one number is one too many. */}
          {/* "Pts: 3" rather than "3 pts", which read as "1 pts" for a question worth one. The label
              leads, so no number has to agree with it. */}
          <span className="aq-points">
            <em>Pts:</em>
            <strong>{props.points}</strong>
          </span>

          {/* The same question drawn again - new numbers where a question has them, a new picture
              or a new set of objects where it does not, which is why the label says neither. A HOTS
              is authored and has nothing to draw again, so the launcher leaves onRefresh out. */}
          {props.onRefresh ? (
            <button
              type="button"
              className="aq-refresh"
              onClick={props.onRefresh}
              disabled={props.refreshDisabled}
              title="Refresh question"
              aria-label="Refresh question"
            >
              ↻
            </button>
          ) : null}

          {/* The card is a thumbnail - parts of a question are sized against the whole window, so
              they only sit right at full size. This opens the real play screen for this one. */}
          {props.onExpand ? (
            <button
              type="button"
              className="aq-expand"
              onClick={props.onExpand}
              title="View in full screen"
              aria-label="View in full screen"
            >
              ⤢
            </button>
          ) : null}
        </div>
      </div>

      <div className="aq-card-body">
        <QuestionFrame
          response={props.response}
          showAnswer={props.showAnswer}
          expanded={props.expanded}
          onCollapse={props.onCollapse}
          onRefresh={props.onRefresh}
          refreshing={props.refreshDisabled}
        />
      </div>
    </div>
  );
}

export default QuestionCard;
